import { useState, useEffect, useCallback } from 'react';

/**
 * useCompanyContext
 * 
 * Reactive hook that tracks the active company/store context across the app.
 * When user switches company via CompanySwitcher, this hook detects the storage
 * change and updates component state, triggering re-fetches in dependent useEffect hooks.
 * 
 * Usage:
 * ```javascript
 * const { accountingClientId, storeId, contextKind } = useCompanyContext();
 * 
 * useEffect(() => {
 *   if (accountingClientId) {
 *     fetchReport(accountingClientId);
 *   }
 * }, [accountingClientId]); // Re-runs when company switches
 * ```
 * 
 * @returns {Object} { accountingClientId, storeId, contextKind }
 */
export const useCompanyContext = () => {
  const [accountingClientId, setAccountingClientId] = useState(() =>
    localStorage.getItem('accountingClientId') || null
  );
  const [storeId, setStoreId] = useState(() =>
    localStorage.getItem('storeId') || null
  );
  const [contextKind, setContextKind] = useState(() =>
    localStorage.getItem('contextKind') || null
  );

  // Listen for storage changes (when another tab/component updates localStorage)
  // This fires when Layout.jsx updates localStorage on company switch
  const handleStorageChange = useCallback((event) => {
    // Keys that matter for company context
    const relevantKeys = ['accountingClientId', 'storeId', 'contextKind'];

    if (event.key && relevantKeys.includes(event.key)) {
      if (event.key === 'accountingClientId') {
        setAccountingClientId(event.newValue || null);
      } else if (event.key === 'storeId') {
        setStoreId(event.newValue || null);
      } else if (event.key === 'contextKind') {
        setContextKind(event.newValue || null);
      }
    }

    // Also handle the case where event.key is null (full storage clear)
    if (event.key === null) {
      setAccountingClientId(localStorage.getItem('accountingClientId') || null);
      setStoreId(localStorage.getItem('storeId') || null);
      setContextKind(localStorage.getItem('contextKind') || null);
    }
  }, []);

  useEffect(() => {
    // Listen to storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen to custom storage change events fired by same-tab updates
    // (localStorage events don't fire in the same tab that modifies it)
    const handleCustomEvent = () => {
      setAccountingClientId(localStorage.getItem('accountingClientId') || null);
      setStoreId(localStorage.getItem('storeId') || null);
      setContextKind(localStorage.getItem('contextKind') || null);
    };
    window.addEventListener('company-context:changed', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('company-context:changed', handleCustomEvent);
    };
  }, [handleStorageChange]);

  return { accountingClientId, storeId, contextKind };
};

export default useCompanyContext;
